import _ from "lodash";
import EditIcon from "@material-ui/icons/Edit";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router";
import BackButton from "../../components/BackButton";
import Button from "../../components/Button";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionReminderCard from "../../components/SessionReminderCard";
import Stepper from "../../components/Stepper";
import SubjectListWithTarget from "../../components/SubjectListWithTarget";
import {
  DeleteSessionStatus,
  Framework,
  SessionStatus,
} from "../../config/Config";
import {
  IsCampaignManager,
  IsSessionCoach,
  IsSessionCoachee,
  IsSessionReadOnly,
} from "../../config/Roles";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { useNotification } from "../../context/notification";
import { utcToLocal, dayJS } from "../../utils/datetime";
import styles from "./SessionsGrowFormDetails.module.scss";

import GetSessionData from "../../services/getSessionData";
import GetReportingManagerForSession from "../../services/getSessionReportingManager";
import { useTranslation } from "react-i18next";
import EditCoachingDate from "../../components/EditCoachingDate";
import SessionDeleteButton from "../../components/SessionDeleteButton";

dayjs.extend(utc);

export function SessionsGrowFormDetails() {
  const history = useHistory();
  const { id } = useParams();
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const [subjectList, setSubjectList] = useState([]);
  const [sessionData, setSessionData] = useState({});
  const [reportingManager, setReportingManager] = useState(null);
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [sessionReviewDate, setSessionReviewDate] = useState("");
  const [sessionCoach, setSessionCoach] = useState(null);
  const [sessionCoachee, setSessionCoachee] = useState(null);
  const [isCoachingDate, setIsCoachingDate] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isDateEdited, setIsDateEdited] = useState(false);
  const [coachingDateHistoryList, setCoachingDateHistoryList] = useState([]);
  const [hideProjectTargetKpi, sethideProjectTargetKpi] = useState(false);
  const [hideCurrentMetrics, sethideCurrentMetricsi] = useState(false);
  const [hideTargetMetrics, sethhideTargetMetrics] = useState(false);
  // fetch sessionDetails and subjectList
  useEffect(() => {
    async function fetchSessionData() {
      const sessionAPIData = await GetSessionData(id);
      const reportingManagerAPIData = await GetReportingManagerForSession(id);
      if (sessionAPIData) {
        setSessionData(sessionAPIData.session);
        if (
          sessionAPIData?.coaching_date_history &&
          sessionAPIData.coaching_date_history.length > 0
        ) {
          setCoachingDateHistoryList(sessionAPIData.coaching_date_history);
        }
      } else {
        addNotification(
          `Could not get session details from id #${id}`,
          "error"
        );
      }
      if (reportingManagerAPIData) {
        setReportingManager(reportingManagerAPIData);
      }
    }

    fetchSessionData();
    localStorage.removeItem("sessionCoachingFormData");
  }, []);

  const isSpecialSession = sessionData?.session_type_id === 3;

  useEffect(() => {
    if (sessionData && sessionData.session_type_id === 2) {
      sethideProjectTargetKpi(true);
      sethideCurrentMetricsi(true);
      sethhideTargetMetrics(true);
    } else {
      sethideProjectTargetKpi(false);
      sethideCurrentMetricsi(false);
      sethhideTargetMetrics(false);
    }
  }, [sessionData]);

  // fetch growFormDetails
  useEffect(() => {
    async function fetchGrowFormDetails() {
      const response = await apiRequest.get(
        `/api/getGrowformDetails?session_id=${id}`
      );

      if (response.status === 200) {
        const data = response.data.data;

        setSubjectList(data.subject);
        setGrowFormDetails(data.grow_form_static_data);
      } else {
        addNotification(
          t("error.couldNotGetGrowFormDetailsFromSessionId") +
            ` #${id}. (Status code: ${response.status})`,
          "error"
        );
      }
    }

    fetchGrowFormDetails();
  }, []);

  // fetch session review date
  useEffect(() => {
    apiRequest
      .get(`/api/getSessionReviewDate?session_id=${id}`)
      .then(res => {
        if (res && res.data.data) {
          let newDate = res.data.data.current_review_date[0].review_datetime;
          newDate = dayjs(new Date(newDate)).format("DD MMM YYYY"); // convert UTC time to local time
          setSessionReviewDate(newDate);
        }
      })
      .finally(() => {});
  }, []);

  const getSessionValue = field => {
    return sessionData ? sessionData[field] : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  useEffect(() => {
    setSessionCoach(IsSessionCoach(getSessionValue("coach_id")));
    setSessionCoachee(IsSessionCoachee(getSessionValue("coachee_id")));
    setIsScheduled(getSessionValue("status") === SessionStatus.SCHEDULED);

    const sessionCoachingDate = utcToLocal(sessionData.session_datetime).format(
      "DD MMM YYYY"
    );
    const sessionReviewDate = utcToLocal(sessionData.review_datetime)
      .add(-2, "day")
      .format("DD MMM YYYY");
    const today = dayJS(new Date()).format("DD MMM YYYY");

    if (process.env.REACT_APP_COACHING_WEB_DEBUGGER) {
    }

    if (sessionData["framework"] === Framework.PD) {
      setIsCoachingDate(new Date(today) >= new Date(sessionCoachingDate));
    } else {
      setIsCoachingDate(
        new Date(today) >= new Date(sessionCoachingDate) &&
          new Date(today) <= new Date(sessionReviewDate)
      );
    }
  }, [sessionData]);

  const isReadOnly = useMemo(() => {
    if (_.isEmpty(sessionData)) return true;
    return IsSessionReadOnly(sessionData.coach_id, sessionData.coachee_id);
  }, [sessionData]);

  return (
    <DashboardLayout
      appBarContent={
        <>
          {sessionData && (
            <Stepper
              isPDFlow={sessionData["framework"] === Framework.PD}
              phase={t("header.stepper.plan")}
              phaseLabel="Plan"
              sessionId={id}
              clockRun={false}
              getTotalTimeSpent={getTotalTime}
            />
          )}
        </>
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={sessionData["coach_id"] === parseInt(getLocalValue("uid"))}
          accessible={!isReadOnly}
        />
      }
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <div className={styles.detailsContainer}>
              <BackButton text={t("common.backToSession")} />
              <SectionTitle
                append={
                  <>
                    {sessionCoachee && isScheduled && (
                      <div
                        className={styles.sectionTitleEditBtn}
                        onClick={() => {
                          history.push(`/sessions/grow/${id}?edit=true`);
                        }}
                      >
                        <EditIcon /> {t("growForm.edit")}
                      </div>
                    )}
                    {sessionCoach && isScheduled && (
                      <div
                        className={styles.sectionTitleEditBtn}
                        onClick={() => {
                          setEditModalOpen(true);
                        }}
                      >
                        <EditIcon /> Edit Session Dates
                      </div>
                    )}
                    {getLocalValue("employee_id") === reportingManager &&
                      IsCampaignManager() &&
                      DeleteSessionStatus.includes(sessionData?.status) && (
                        <SessionDeleteButton sessionId={id} />
                      )}
                  </>
                }
              >
                {isSpecialSession ? (
                  <> Plan Based On Conversational Model </>
                ) : (
                  <>GROW Model</>
                )}
              </SectionTitle>
              {sessionData && sessionData["framework"] !== Framework.PD && (
                <SessionReminderCard
                  plain
                  header={t("growForm.yourReviewSessionIsOn")}
                  date={sessionReviewDate}
                />
              )}
              {sessionData && isSpecialSession ? (
                <>
                  <div className={styles.sectionlable}>
                    {t("growForm.projectTargetKpi")}
                  </div>
                  <div className={styles.rowRight}>
                    <SubjectListWithTarget
                      hideProjectTargetKpi={hideProjectTargetKpi}
                      hideCurrentMetrics={hideCurrentMetrics}
                      hideTargetMetrics={hideTargetMetrics}
                      list={
                        subjectList && subjectList.length > 0 ? subjectList : []
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.goals")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>
                          {t("growForm.knowingYourCoachingSubjectsAndCreate")}
                        </p>
                        <p>{t("growForm.SMARTGolasIsAnEstablished")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.content}>
                        <p>
                          {growFormDetails && growFormDetails.goal
                            ? growFormDetails.goal
                            : ""}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.reality")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.knowingYourCurrentPerformance")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <SubjectListWithTarget
                        hideProjectTargetKpi={hideProjectTargetKpi}
                        hideCurrentMetrics={hideCurrentMetrics}
                        hideTargetMetrics={hideTargetMetrics}
                        list={
                          subjectList && subjectList.length > 0
                            ? subjectList
                            : []
                        }
                      />
                      <p>
                        {growFormDetails && growFormDetails.reality_optional
                          ? growFormDetails.reality_optional
                          : ""}
                      </p>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {" "}
                        {t("growForm.options")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>
                          {t("growForm.brainstormForMultipleOptionsToImprove")}
                        </p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div className={styles.content}>
                          <p>
                            {growFormDetails && growFormDetails.opportunity
                              ? growFormDetails.opportunity
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("growForm.wayForward")}
                      </div>
                      <div className={styles.labelDescription}>
                        <p>{t("growForm.selectTheMostFeasibleOptions")}</p>
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.contentContainer}>
                        <div className={styles.content}>
                          <p>
                            {growFormDetails && growFormDetails.way_forward
                              ? growFormDetails.way_forward
                              : ""}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className={styles.detailsContainer}>
              {sessionCoachee && isScheduled && (
                <div className={styles.confirmButtonWrapper}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      history.push(`/sessions/coaching/${id}`);
                    }}
                  >
                    {t("growForm.startCoachingSession")}
                  </Button>
                </div>
              )}
            </div>
          </Section>

          <EditCoachingDate
            isOpen={editModalOpen}
            isScheduled={isScheduled}
            setEditModalOpen={setEditModalOpen}
            sessionDetails={sessionData}
            setIsDateEdited={setIsDateEdited}
            redirectToSessionDetails={true}
            subjectDetails={subjectList}
            setSubjectDetails={setSubjectList}
            coachingDateHistoryList={coachingDateHistoryList}
          />
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
